/**
 * Wave 27.0 — Project create (full-page editor — NOT a tiny modal).
 *
 * Steps in one column: name + customer/site + owner/status + planned
 * dates + budget mode + tags + create.
 */
import { useState, useEffect } from "react";
import VoiceTextarea from "../components/VoiceTextarea";
import { useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, ChevronDown, Briefcase, X, Search,
  Calendar, DollarSign, Users, Tag, Sparkles, Check,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import DatePickerInput from "../components/DatePickerInput";

const STATUSES = [
  { value: "planning", label: "Planning" },
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
];
const BUDGET_SOURCES = [
  { value: "manual", label: "Set manually", hint: "Enter a fixed budget below" },
  { value: "quote_total", label: "From quotes", hint: "Auto-track against the accepted quote totals (internal/DNU preferred)" },
  { value: "sales_order_total", label: "From sales orders", hint: "Auto-track against sales order totals (internal/DNU preferred)" },
  { value: "purchase_order_total", label: "From purchase orders", hint: "Budget = sum of all POs raised on this project" },
  { value: "expense_total", label: "From approved expenses", hint: "Budget = sum of all approved expenses (paid cost)" },
  { value: "committed_cost_total", label: "From committed cost", hint: "POs + approved expenses combined — your true cost commitment" },
];

export default function ProjectNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "",
    customer_id: "",
    customer_name: "",
    site_id: "",
    site_label: "",
    owner_id: user?.id || "",
    owner_name: user?.name || user?.email || "",
    status: "planning",
    description: "",
    planned_start: "",
    planned_end: "",
    budget_source: "manual",
    budget_amount: "",
    budget_currency: "ZAR",
    color: "#1FB6C1",
    tags: [],
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Phase D — Project memory: when customer changes, ask Hidro for a
  // budget/duration prefill from similar past projects.
  const [memory, setMemory] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryApplied, setMemoryApplied] = useState(false);
  useEffect(() => {
    if (!form.customer_id) { setMemory(null); setMemoryApplied(false); return; }
    let cancelled = false;
    setMemoryLoading(true);
    setMemoryApplied(false);
    const kw = form.title.trim();
    api.get("/insights/project-memory/suggest", {
      params: { customer_id: form.customer_id, keywords: kw },
    })
      .then(({ data }) => { if (!cancelled) setMemory(data); })
      .catch(() => { if (!cancelled) setMemory(null); })
      .finally(() => { if (!cancelled) setMemoryLoading(false); });
    return () => { cancelled = true; };
  }, [form.customer_id, form.title]);

  const applyMemory = () => {
    if (!memory?.suggestion) return;
    const sug = memory.suggestion;
    setForm((f) => ({
      ...f,
      budget_amount: sug.suggested_budget ? String(sug.suggested_budget) : f.budget_amount,
      budget_source: "manual",
    }));
    // If any candidate has both planned_start & planned_end, use the
    // average duration to estimate planned_end (today + avg duration).
    try {
      const durations = memory.items
        .filter((p) => p.planned_start && p.planned_end)
        .map((p) => (new Date(p.planned_end) - new Date(p.planned_start)) / 86400000)
        .filter((d) => d > 0 && d < 365);
      if (durations.length > 0) {
        const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
        const today = new Date();
        const end = new Date(today.getTime() + avg * 86400000);
        setForm((f) => ({
          ...f,
          planned_start: f.planned_start || today.toISOString().slice(0, 10),
          planned_end: end.toISOString().slice(0, 10),
        }));
      }
    } catch (_e) {
      // Prefill is best-effort; if memory is malformed the user just
      // doesn't get a prefill (iter 290j — surfaced via console.warn so
      // we can spot recurring shapes in prod).
      console.warn("ProjectNew memory prefill skipped:", _e);
    }
    setMemoryApplied(true);
    toast.success(`Prefilled from ${memory.items.length} similar project${memory.items.length === 1 ? "" : "s"}.`);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Project name is required."); return; }
    if (!form.customer_id) { toast.error("Pick a customer."); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        customer_id: form.customer_id,
        site_id: form.site_id || null,
        owner_id: form.owner_id || null,
        status: form.status,
        description: form.description || "",
        planned_start: form.planned_start || null,
        planned_end: form.planned_end || null,
        budget_source: form.budget_source,
        budget_amount: form.budget_amount ? Number(form.budget_amount) : null,
        budget_currency: form.budget_currency,
        color: form.color,
        tags: form.tags,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(`Project ${data.short_id} created.`);
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="project-new-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-xs" data-testid="proj-new-back">
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#1FB6C1]" /> New project
          </h1>
          <p className="text-xs text-[#647087]">Set up a container for this piece of work — quotes, jobs, expenses all link here.</p>
        </div>
      </div>

      {/* Identity */}
      <section className="hv-card !p-4 space-y-3">
        <Field label="Project name" required>
          <input
            type="text"
            value={form.title}
            onChange={(e) => upd("title", e.target.value)}
            placeholder="e.g. Spar Eastgate RO Upgrade"
            className="hv-input text-sm w-full"
            data-testid="proj-new-title"
            autoFocus
          />
        </Field>
        <Field label="Customer" required>
          <EntityPicker
            kind="customer"
            value={form.customer_id ? { id: form.customer_id, label: form.customer_name } : null}
            onChange={(c) => { upd("customer_id", c?.id || ""); upd("customer_name", c?.label || ""); upd("site_id", ""); upd("site_label", ""); }}
            placeholder="Search customers…"
            testId="proj-new-customer"
          />
        </Field>
        {form.customer_id && (
          <Field label="Site (optional)">
            <EntityPicker
              kind="site"
              customerId={form.customer_id}
              value={form.site_id ? { id: form.site_id, label: form.site_label } : null}
              onChange={(s) => { upd("site_id", s?.id || ""); upd("site_label", s?.label || ""); }}
              placeholder="Search sites…"
              testId="proj-new-site"
            />
          </Field>
        )}
        {/* Phase D — Project memory suggestion banner */}
        {form.customer_id && memory?.suggestion && !memoryApplied && (
          <div
            className="hv-card !p-2.5 border-[#1FB6C1]/40 bg-gradient-to-br from-[#1FB6C1]/[0.08] to-transparent flex items-start gap-2"
            data-testid="proj-new-memory-banner"
          >
            <Sparkles className="w-4 h-4 text-[#1FB6C1] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white">
                Hidro found <strong>{memory.items.length}</strong> similar past project{memory.items.length === 1 ? "" : "s"} for this customer.
              </div>
              <div className="text-[11px] text-[#a0aabf] mt-0.5">
                Suggested budget <span className="text-white font-mono">ZAR {Number(memory.suggestion.suggested_budget).toLocaleString()}</span>
                {memory.suggestion.avg_actual_cost > 0 && (
                  <> · avg actual cost <span className="text-white font-mono">ZAR {Number(memory.suggestion.avg_actual_cost).toLocaleString()}</span></>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={applyMemory}
              className="hv-btn hv-btn-primary text-[11px] shrink-0"
              data-testid="proj-new-apply-memory"
            >
              <Check className="w-3 h-3" /> Use
            </button>
          </div>
        )}
        {memoryLoading && form.customer_id && (
          <div className="text-[11px] text-[#647087] italic inline-flex items-center gap-1 px-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Hidro is checking your project history…
          </div>
        )}
        <Field label="Notes (optional)">
          <VoiceTextarea
            value={form.description}
            onChange={(v) => upd("description", v)}
            placeholder="Short brief, scope, deliverables…"
            rows={3}
            context="project_description"
            testIdPrefix="proj-new-description-voice"
            data-testid="proj-new-description"
          />
        </Field>
      </section>

      {/* Planning */}
      <section className="hv-card !p-4 space-y-3">
        <div className="font-display font-semibold text-sm flex items-center gap-1.5 text-[#a0aabf]">
          <Calendar className="w-3.5 h-3.5" /> Planning
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => upd("status", e.target.value)}
              className="hv-input text-sm w-full"
              data-testid="proj-new-status"
            >
              {STATUSES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
          </Field>
          <Field label="Owner">
            <UserPicker
              value={form.owner_id ? { id: form.owner_id, label: form.owner_name } : null}
              onChange={(u) => { upd("owner_id", u?.id || ""); upd("owner_name", u?.label || ""); }}
              testId="proj-new-owner"
            />
          </Field>
          <Field label="Planned start">
            <DatePickerInput
              value={form.planned_start || ""}
              onChange={(v) => upd("planned_start", v)}
              testId="proj-new-start"
            />
          </Field>
          <Field label="Planned end">
            <DatePickerInput
              value={form.planned_end || ""}
              onChange={(v) => upd("planned_end", v)}
              testId="proj-new-end"
            />
          </Field>
        </div>
      </section>

      {/* Budget */}
      <section className="hv-card !p-4 space-y-3">
        <div className="font-display font-semibold text-sm flex items-center gap-1.5 text-[#a0aabf]">
          <DollarSign className="w-3.5 h-3.5" /> Budget tracking
        </div>
        <div className="flex flex-col gap-2">
          {BUDGET_SOURCES.map((b) => (
            <label key={b.value} className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${form.budget_source === b.value ? "border-[#1FB6C1] bg-[#1FB6C1]/5" : "border-white/10 hover:bg-white/[0.04]"}`}>
              <input
                type="radio"
                name="budget_source"
                checked={form.budget_source === b.value}
                onChange={() => upd("budget_source", b.value)}
                className="mt-1"
                data-testid={`proj-new-budget-${b.value}`}
              />
              <div className="text-sm">
                <div className="font-medium text-white">{b.label}</div>
                <div className="text-[11px] text-[#647087]">{b.hint}</div>
              </div>
            </label>
          ))}
        </div>
        {form.budget_source === "manual" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Currency">
              <select
                value={form.budget_currency}
                onChange={(e) => upd("budget_currency", e.target.value)}
                className="hv-input text-sm w-full"
              >
                {["ZAR", "USD", "EUR", "GBP"].map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <div className="col-span-2">
              <Field label="Manual budget amount">
                <input
                  type="number"
                  step="0.01"
                  value={form.budget_amount}
                  onChange={(e) => upd("budget_amount", e.target.value)}
                  placeholder="0.00"
                  className="hv-input text-sm w-full"
                  data-testid="proj-new-budget-amount"
                />
              </Field>
            </div>
          </div>
        )}
        {form.budget_source !== "manual" && (
          <div className="text-[11px] text-[#647087] italic">
            Budget will be the sum of {form.budget_source === "quote_total" ? "accepted quotes" : "sales orders"} linked to this project. You can override later from the project page.
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-sm" data-testid="proj-new-cancel">Cancel</button>
        <button
          onClick={submit}
          disabled={busy || !form.title.trim() || !form.customer_id}
          className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
          data-testid="proj-new-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Briefcase className="w-3.5 h-3.5" />}
          Create project
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
        {label}{required && <span className="text-rose-400"> *</span>}
      </div>
      {children}
    </div>
  );
}

/** Generic entity picker — customers / sites. */
export function EntityPicker({ kind, customerId, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let endpoint = `/${kind}s`;
        let params = { q: query || "", limit: 12 };
        if (kind === "site") {
          if (!customerId) { setResults([]); setSearching(false); return; }
          endpoint = `/sites`;
          params = { customer_id: customerId, limit: 50 };
        } else if (kind === "project") {
          endpoint = `/projects`;
          params = { q: query || "", limit: 20 };
        }
        const { data } = await api.get(endpoint, { params, signal: ctl.signal });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setResults(arr.slice(0, 12));
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally { setSearching(false); }
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [open, query, kind, customerId]);

  const labelFor = (it) => {
    if (kind === "project") return `${it.short_id || ""} · ${it.title || it.id}`.trim();
    return it.name || it.trading_name || it.label || it.title || it.id;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input text-sm w-full flex items-center justify-between gap-2"
        data-testid={testId}
      >
        <span className={value?.id ? "text-white truncate" : "text-[#647087]"}>{value?.label || placeholder}</span>
        <div className="flex items-center gap-1 shrink-0">
          {value?.id && (
            <X
              className="w-3.5 h-3.5 text-[#647087] hover:text-white"
              onClick={(e) => { e.stopPropagation(); onChange(null); setQuery(""); }}
            />
          )}
          <ChevronDown className="w-3.5 h-3.5 text-[#647087]" />
        </div>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0e1015] border border-white/10 rounded shadow-xl max-h-72 overflow-y-auto p-1.5">
          <div className="relative mb-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${kind}s…`}
              className="hv-input text-xs pl-7 w-full"
              autoFocus
              data-testid={`${testId}-search`}
            />
            {searching && <Loader2 className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
          </div>
          {results.length === 0 && !searching ? (
            <div className="text-[11px] text-[#647087] py-3 text-center">No matches.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onChange({ id: r.id, label: labelFor(r) }); setOpen(false); setQuery(""); }}
                className="w-full text-left text-xs p-1.5 rounded hover:bg-white/[0.04]"
                data-testid={`${testId}-result-${r.id}`}
              >
                <div className="font-medium text-white">{labelFor(r)}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function UserPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/users", { params: { q: query, limit: 20 } })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setUsers(arr);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open, query]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input text-sm w-full flex items-center justify-between gap-2"
        data-testid={testId}
      >
        <span className={value?.id ? "text-white" : "text-[#647087]"}>{value?.label || "Pick owner"}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#647087]" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0e1015] border border-white/10 rounded shadow-xl max-h-72 overflow-y-auto p-1.5">
          <div className="relative mb-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users…"
              className="hv-input text-xs pl-7 w-full"
              autoFocus
            />
            {loading && <Loader2 className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
          </div>
          {users.length === 0 && !loading ? (
            <div className="text-[11px] text-[#647087] py-3 text-center">No users.</div>
          ) : (
            users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => { onChange({ id: u.id, label: u.name || u.email }); setOpen(false); setQuery(""); }}
                className="w-full text-left text-xs p-1.5 rounded hover:bg-white/[0.04]"
              >
                <div className="font-medium text-white">{u.name || u.email}</div>
                <div className="text-[10px] text-[#647087]">{u.email} · {u.role}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
